import type { CSSProperties } from 'react'

interface Props {
  szerokosc?: string
  wysokosc?: string
  kolo?: boolean
  styl?: CSSProperties
}

/**
 * Pojedynczy prostokąt zastępczy. Szkielety mają odwzorowywać układ, który
 * za chwilę się pojawi — dzięki temu treść nie przeskakuje po wczytaniu,
 * a oko wie, gdzie patrzeć.
 */
export function Szkielet({ szerokosc = '100%', wysokosc = '12px', kolo = false, styl }: Props) {
  return (
    <span
      className={kolo ? 'szkielet szkielet--kolo' : 'szkielet'}
      style={{ display: 'block', width: szerokosc, height: wysokosc, ...styl }}
    />
  )
}

/** Trzy karty liczników w dokładnie tych samych wymiarach co prawdziwe. */
export function SzkieletLicznikow() {
  return (
    <section className="pulpit__liczniki" aria-hidden="true">
      {[0, 1, 2].map((indeks) => (
        <article className="licznik karta" key={indeks}>
          <header className="licznik__naglowek">
            <Szkielet szerokosc="88px" wysokosc="11px" />
            <Szkielet szerokosc="64px" wysokosc="10px" />
          </header>
          <Szkielet szerokosc="62%" wysokosc="32px" styl={{ borderRadius: 'var(--r-2)' }} />
          <Szkielet wysokosc="5px" styl={{ borderRadius: 'var(--r-pelny)' }} />
          <div className="licznik__rozbicie">
            <Szkielet szerokosc="74px" wysokosc="26px" />
            <Szkielet szerokosc="74px" wysokosc="26px" />
          </div>
        </article>
      ))}
    </section>
  )
}

/** Wiersze listy płatności — ta sama siatka, więc nic nie drgnie po podmianie. */
export function SzkieletListy({ ile = 4 }: { ile?: number }) {
  return (
    <ul className="platnosci" aria-hidden="true">
      {Array.from({ length: ile }, (_, indeks) => (
        <li className="platnosc" key={indeks}>
          <Szkielet szerokosc="44px" wysokosc="42px" styl={{ borderRadius: 'var(--r-1)' }} />
          <div style={{ display: 'grid', gap: '6px' }}>
            <Szkielet szerokosc={`${52 + ((indeks * 13) % 28)}%`} wysokosc="13px" />
            <Szkielet szerokosc="38%" wysokosc="10px" />
          </div>
          <Szkielet szerokosc="72px" wysokosc="14px" />
          <Szkielet szerokosc="32px" wysokosc="32px" kolo />
        </li>
      ))}
    </ul>
  )
}

export function SzkieletWykresuKategorii() {
  return (
    <div className="wykres-kategorii" aria-hidden="true">
      <Szkielet szerokosc="148px" wysokosc="148px" kolo styl={{ flex: 'none' }} />
      <div style={{ display: 'grid', gap: '10px', width: '100%' }}>
        {[0, 1, 2, 3].map((indeks) => (
          <Szkielet key={indeks} wysokosc="13px" szerokosc={`${94 - indeks * 14}%`} />
        ))}
      </div>
    </div>
  )
}

export function SzkieletWykresuTrendu() {
  // Wysokości udają nierówny przebieg roku — płaski rząd wyglądałby na błąd.
  const wysokosci = [38, 52, 44, 70, 58, 82, 64, 96, 74, 60, 88, 46]
  return (
    <div className="wykres-trendu" aria-hidden="true">
      {wysokosci.map((wysokosc, indeks) => (
        <div className="wykres-trendu__kolumna" key={indeks}>
          <div className="wykres-trendu__slupek">
            <Szkielet wysokosc={`${wysokosc}%`} styl={{ borderRadius: 'var(--r-1)' }} />
          </div>
          <Szkielet szerokosc="60%" wysokosc="9px" styl={{ margin: '0 auto' }} />
        </div>
      ))}
    </div>
  )
}

export function SzkieletPozycji({ ile = 6 }: { ile?: number }) {
  return (
    <ul className="pozycje" aria-hidden="true">
      {Array.from({ length: ile }, (_, indeks) => (
        <li className="pozycja" key={indeks}>
          <span className="pozycja__glowna">
            <span style={{ display: 'grid', gap: '6px', flex: 1 }}>
              <Szkielet szerokosc={`${40 + ((indeks * 17) % 34)}%`} wysokosc="13px" />
              <Szkielet szerokosc="26%" wysokosc="10px" />
            </span>
            <span style={{ display: 'grid', gap: '6px', justifyItems: 'end' }}>
              <Szkielet szerokosc="76px" wysokosc="13px" />
              <Szkielet szerokosc="58px" wysokosc="9px" />
            </span>
          </span>
        </li>
      ))}
    </ul>
  )
}

/** Siatka kalendarza w docelowych proporcjach. */
export function SzkieletKalendarza() {
  return (
    <div className="kalendarz" aria-hidden="true">
      <div className="kalendarz__naglowki">
        {Array.from({ length: 7 }, (_, indeks) => (
          <Szkielet key={indeks} szerokosc="60%" wysokosc="9px" styl={{ margin: '0 auto' }} />
        ))}
      </div>
      <div className="kalendarz__siatka">
        {Array.from({ length: 35 }, (_, indeks) => (
          <Szkielet
            key={indeks}
            wysokosc="100%"
            styl={{ minHeight: '58px', borderRadius: 'var(--r-1)' }}
          />
        ))}
      </div>
    </div>
  )
}
